import { useEffect } from 'react';
import type { ContactFormFields } from '@transcend-io/contact-form-schema';
import { snakeCase } from 'change-case';
import { AnimatePresence, motion } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import type { Schema } from 'zod';

import type { ResponseError } from '#/lib/api/types';
import {
  ChiliPiperProvider,
  SchedulingStateEnum,
  useChiliPiperModal,
} from '#/lib/chili-piper/ChiliPiperContext';
import { checkFormServerError } from '#/lib/contact-form-client/helpers';
import { submitLead } from '#/lib/contact-form-client/submit';
import type { SanityDefaultForm } from '#/lib/sanity/queries/form';
import { useAnalytics, useConversionEvent } from '#/lib/segment';

import { useFormMetadata } from './utils';

type Props<T extends Schema<ContactFormFields>> = {
  pardotCampaignId: SanityDefaultForm['pardotCampaignId'];
  pardotListIds: SanityDefaultForm['pardotListIds'];
  formFieldsSchema: T;
  trackingEventName: SanityDefaultForm['trackingEventName'];
  ConfirmationComponent: React.FC<{ isWaitingOnSchedulingLoad: boolean }>;
  enableScheduling: SanityDefaultForm['enableScheduling'];
  onSubmitSuccess?: () => void;
  onSubmitError?: ({ error }: ResponseError) => void;
  /**
   * The form interior
   *
   * This should at a minimum have render logic for these states:
   * - Form is submitting: `ctx.formState.isSubmitting`
   * - Server error: `formState.errors.root?.type === 'server'` (see `checkFormServerError` helper)
   */
  children: (ctx: UseFormReturn<ContactFormFields>) => React.ReactNode;
};

function RawForm<T extends Schema<ContactFormFields>>({
  pardotCampaignId,
  pardotListIds,
  formFieldsSchema,
  trackingEventName,
  ConfirmationComponent,
  onSubmitSuccess,
  onSubmitError,
  children,
  enableScheduling,
}: Props<T>) {
  const { initSchedulingModal, waitForSchedulingLoad, schedulingState } =
    useChiliPiperModal();

  const { methods, getMetadata } = useFormMetadata({
    pardotCampaignId,
    pardotListIds,
    formFieldsSchema,
  });

  const fireConversionEvent = useConversionEvent();
  const analytics = useAnalytics();

  // Consent to be contacted is implied in the contact form
  useEffect(() => methods.setValue('consent', true), [methods]);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit: SubmitHandler<ContactFormFields> = async (fields) => {
    try {
      fireConversionEvent(fields.email, trackingEventName);

      // Execute reCAPTCHA and add to the contact form body
      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha(snakeCase(trackingEventName)));

      const metadata = getMetadata(recaptchaToken);

      if (enableScheduling) {
        initSchedulingModal(fields);
      }

      const response = await submitLead(fields, metadata, { analytics });

      // Server responded with an error
      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        const serverError = checkFormServerError(methods.formState);
        if (serverError) {
          onSubmitError?.({
            error: serverError.message,
          });
        }
      } else {
        if (enableScheduling) {
          // Wait for either scheduling to load, disqualify, or timeout
          await waitForSchedulingLoad();
        }

        onSubmitSuccess?.();
      }
    } catch (err) {
      // Something else went wrong
      console.error(err);
      methods.setError('root', {
        type: 'server',
      });
      const serverError = checkFormServerError(methods.formState);
      if (serverError) {
        onSubmitError?.({
          error: serverError.message,
        });
      }
    }
  };

  return (
    <div className="relative">
      <AnimatePresence mode="popLayout">
        {methods.formState.isSubmitSuccessful ? (
          <motion.div
            key="confirmation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ConfirmationComponent
              isWaitingOnSchedulingLoad={
                enableScheduling &&
                schedulingState.status === SchedulingStateEnum.ROUTING
              }
            />
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handleSubmit)}>
                {children(methods)}
              </form>
            </FormProvider>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Form<T extends Schema<ContactFormFields>>(props: Props<T>) {
  return (
    <ChiliPiperProvider enableScheduling={props.enableScheduling}>
      <RawForm {...props} />
    </ChiliPiperProvider>
  );
}
